import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  effect,
  inject,
  input,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { WidgetService } from '../../services/widget.service';
import { TourBookingService } from '../../services/tour-booking.service';
import { TourAvailabilityResponse } from '../../interfaces/tour-booking.interface';
import { ClassDate } from '../../interfaces/date.interface';
import { LeadCaptureV2Component } from '../lead-capture-v2/lead-capture-v2.component';
import { LeadCaptureWidgetSubmission } from '../../interfaces/lead-capture-submission.interface';
import { extractWidgetApiErrorMessage } from '../../services/widget-api-error.util';
import {
  applyDefaultPurchasePortalTheme,
  applyPurchasePortalThemeToHost,
  buildPurchasePortalTheme,
  parsePurchasePortalTheme,
} from '../../services/widget-purchase-portal-theme.util';
import { WidgetRecaptchaFlowService } from '../../services/widget-recaptcha-flow.service';
import { WidgetRecaptchaSubmitPayload } from '../../services/recaptcha/widget-recaptcha.types';
import { WidgetRecaptchaV2SectionComponent } from '../../components/widget-recaptcha-v2-section/widget-recaptcha-v2-section.component';
import { WidgetProcessingOverlayComponent } from '../../components/widget-processing-overlay/widget-processing-overlay.component';
import { WIDGET_RECAPTCHA_V2_HINT_TOUR_BOOKING } from '../../constants/checkout-security.constants';

interface LoadingState {
  widget: boolean;
  availability: boolean;
  submission: boolean;
}

interface ErrorState {
  widget?: string;
  availability?: string;
  submission?: string;
}

@Component({
  selector: 'ufp-tour-booking',
  templateUrl: './tour-booking.component.html',
  styleUrls: ['./tour-booking.component.css'],
  // Emulated (not ShadowDom): reCAPTCHA v2 iframes/callbacks fail inside closed shadow trees.
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LeadCaptureV2Component,
    WidgetRecaptchaV2SectionComponent,
    WidgetProcessingOverlayComponent,
  ],
})
export class TourBookingComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly widgetId = input('');

  private readonly leadCapture = viewChild(LeadCaptureV2Component);
  private readonly formColumn = viewChild<ElementRef<HTMLElement>>('formColumn');
  private readonly slotColumn = viewChild<ElementRef<HTMLElement>>('slotColumn');

  private readonly widgetService = inject(WidgetService);
  private readonly tourBookingService = inject(TourBookingService);
  readonly recaptchaFlow = inject(WidgetRecaptchaFlowService);
  readonly recaptchaV2Hint = WIDGET_RECAPTCHA_V2_HINT_TOUR_BOOKING;
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);

  /** HttpClient callbacks can run outside NgZone in embed/custom-element apps. */
  private notifyViewUpdate(): void {
    this.ngZone.run(() => this.cdr.detectChanges());
  }

  loading: LoadingState = {
    widget: false,
    availability: false,
    submission: false,
  };
  error: ErrorState = {};
  success = false;
  selectedTenantUuid: string | null = null;
  companyUuid: string | null = null;

  dates: ClassDate[] = [];
  selectedDateIndex = 0;
  dateScrollIndex = 0;
  visibleDatesCount = 8;

  availabilityResponse: TourAvailabilityResponse | null = null;
  availableSlots: string[] = [];
  selectedSlot: string | null = null;
  surveyFormComplete = false;

  private widgetInitialized = false;
  private readonly availabilityWindowDays = 7;
  private availabilityLoadedStart: string | null = null;
  private availabilityLoadedEnd: string | null = null;
  private formColumnResizeObserver?: ResizeObserver;
  private slotColumnLayoutMediaQuery: MediaQueryList | null = null;
  private previousWidgetId: string | undefined;

  constructor() {
    effect(() => {
      const id = this.widgetId();
      if (
        this.previousWidgetId !== undefined &&
        id !== this.previousWidgetId
      ) {
        this.widgetInitialized = false;
        this.tryInitializeWidget();
      }
      this.previousWidgetId = id;
    });
  }

  ngOnInit() {
    applyDefaultPurchasePortalTheme(this.elementRef.nativeElement);
    this.loadDates();
    this.tryInitializeWidget();
  }

  ngAfterViewInit(): void {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.slotColumnLayoutMediaQuery = window.matchMedia('(max-width: 768px)');
      this.slotColumnLayoutMediaQuery.addEventListener(
        'change',
        this.syncSlotColumnMaxHeight,
      );
    }

    this.setupSlotColumnHeightSync();
  }

  ngOnDestroy(): void {
    this.formColumnResizeObserver?.disconnect();
    this.slotColumnLayoutMediaQuery?.removeEventListener(
      'change',
      this.syncSlotColumnMaxHeight,
    );
  }

  private setupSlotColumnHeightSync(): void {
    const formElement = this.formColumn()?.nativeElement;
    if (!formElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.syncSlotColumnMaxHeight();
    this.formColumnResizeObserver = new ResizeObserver(() => {
      this.syncSlotColumnMaxHeight();
    });
    this.formColumnResizeObserver.observe(formElement);
  }

  private readonly syncSlotColumnMaxHeight = (): void => {
    const formElement = this.formColumn()?.nativeElement;
    const slotElement = this.slotColumn()?.nativeElement;
    if (!formElement || !slotElement) {
      return;
    }

    if (this.slotColumnLayoutMediaQuery?.matches) {
      slotElement.style.maxHeight = '';
      return;
    }

    slotElement.style.maxHeight = `${formElement.offsetHeight}px`;
  };

  private tryInitializeWidget(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.widgetId()) {
      this.error.widget = 'Widget ID is required';
      return;
    }

    if (this.widgetInitialized) {
      return;
    }

    this.widgetInitialized = true;
    this.error.widget = '';
    this.initializeWidget();
  }

  private async initializeWidget() {
    this.loading.widget = true;
    try {
      const widgetDetails = await firstValueFrom(
        this.widgetService.getWidgetDetails(this.widgetId()),
      );
      if (widgetDetails.widget_type !== 'TourBooking') {
        throw new Error('Invalid widget type. Expected tour-booking.');
      }

      this.companyUuid = widgetDetails.widget_company_uuid;
      this.selectedTenantUuid =
        widgetDetails.widget_enabled_tenants[0]?.tenant_uuid ?? null;
      if (this.companyUuid && this.selectedTenantUuid) {
        void this.recaptchaFlow.prepareForTenant(
          this.companyUuid,
          this.selectedTenantUuid,
        );
      }
      await this.applyCompanyTheme(widgetDetails.widget_company_uuid);
      this.loadAvailability();
    } catch {
      this.error.widget = 'Failed to initialize widget. Please try again later.';
    } finally {
      this.loading.widget = false;
      this.notifyViewUpdate();
    }
  }

  /** Apply purchase portal theme from company API (same as one-page-checkout). */
  private async applyCompanyTheme(companyUuid: string): Promise<void> {
    if (!companyUuid) {
      return;
    }

    try {
      const company = await firstValueFrom(
        this.widgetService.getCompanyDetails(companyUuid),
      );
      const rawTheme = company.purchase_portal_theme;

      if (rawTheme == null || rawTheme === '') {
        return;
      }

      const companyTheme = parsePurchasePortalTheme(
        rawTheme as string | Record<string, unknown>,
      );
      const queryParams = this.readEmbedQueryParams();
      const { theme, isDark } = buildPurchasePortalTheme(
        companyTheme,
        queryParams,
      );

      applyPurchasePortalThemeToHost(
        this.elementRef.nativeElement,
        theme,
        isDark,
      );
    } catch {
      // Company theme is optional.
    }
  }

  private readEmbedQueryParams(): Record<string, string> {
    if (typeof window === 'undefined') {
      return {};
    }

    const queryParams: Record<string, string> = {};
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      queryParams[key] = value;
    }

    return queryParams;
  }

  private loadDates() {
    const today = new Date();
    const dates: ClassDate[] = [];

    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      dates.push({
        date,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: date.getDate(),
        monthName: date.toLocaleDateString('en-US', { month: 'short' }),
        isVisible: i < this.visibleDatesCount,
      });
    }

    this.dates = dates;
  }

  private loadAvailability(): void {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + this.availabilityWindowDays - 1);
    this.fetchAvailability(startDate, endDate);
  }

  private fetchAvailability(rangeStart: Date, rangeEnd: Date): void {
    if (!this.selectedTenantUuid || !this.companyUuid) {
      this.error.availability = 'No tenant selected';
      this.notifyViewUpdate();
      return;
    }

    this.loading.availability = true;
    this.error.availability = '';
    this.notifyViewUpdate();

    const startKey = this.formatDateKey(rangeStart);
    const endKey = this.formatDateKey(rangeEnd);

    this.tourBookingService
      .getTourAvailability(
        this.companyUuid,
        this.selectedTenantUuid,
        startKey,
        endKey,
      )
      .subscribe({
        next: (response) => {
          this.availabilityResponse = response;
          this.availabilityLoadedStart = startKey;
          this.availabilityLoadedEnd = endKey;
          this.updateSlotsForSelectedDate();
          this.loading.availability = false;
          this.notifyViewUpdate();
        },
        error: () => {
          this.error.availability =
            'Failed to load available slots. Please try again later.';
          this.loading.availability = false;
          this.notifyViewUpdate();
        },
      });
  }

  private isDateWithinAvailabilityRange(dateKey: string): boolean {
    if (!this.availabilityLoadedStart || !this.availabilityLoadedEnd) {
      return false;
    }

    return (
      dateKey >= this.availabilityLoadedStart &&
      dateKey <= this.availabilityLoadedEnd
    );
  }

  private getAvailabilityRangeEnd(selectedDate: Date): Date {
    const rangeEnd = new Date(selectedDate);
    rangeEnd.setDate(rangeEnd.getDate() + this.availabilityWindowDays - 1);

    const pickerLastDate = this.dates[this.dates.length - 1]?.date;
    if (pickerLastDate && rangeEnd > pickerLastDate) {
      return new Date(pickerLastDate);
    }

    return rangeEnd;
  }

  onDateSelect(index: number) {
    if (this.selectedDateIndex === index) {
      return;
    }

    this.selectedDateIndex = index;
    this.selectedSlot = null;

    const selectedDate = this.dates[index].date;
    const dateKey = this.formatDateKey(selectedDate);

    if (this.isDateWithinAvailabilityRange(dateKey)) {
      this.updateSlotsForSelectedDate();
      this.notifyViewUpdate();
      return;
    }

    this.fetchAvailability(selectedDate, this.getAvailabilityRangeEnd(selectedDate));
  }

  private updateSlotsForSelectedDate() {
    if (!this.availabilityResponse || this.dates.length === 0) {
      this.availableSlots = [];
      return;
    }

    const selectedDate = this.dates[this.selectedDateIndex].date;
    const dateKey = this.formatDateKey(selectedDate);
    let slots = this.tourBookingService.getConsolidatedSlots(
      this.availabilityResponse,
      dateKey,
    );

    if (this.isSelectedDateToday(dateKey)) {
      slots = this.filterPastSlots(slots);
    }

    this.availableSlots = slots;

    if (this.selectedSlot && !this.availableSlots.includes(this.selectedSlot)) {
      this.selectedSlot = null;
    }
  }

  private isSelectedDateToday(dateKey: string): boolean {
    if (dateKey === this.formatDateKey(new Date())) {
      return true;
    }

    const apiTodayKey = this.availabilityResponse?.today?.slice(0, 10);
    return !!apiTodayKey && dateKey === apiTodayKey;
  }

  private filterPastSlots(slots: string[]): string[] {
    const nowMinutes = this.getMinutesSinceMidnight(new Date());

    return slots.filter((slot) => {
      const slotDate = new Date(slot);
      if (Number.isNaN(slotDate.getTime())) {
        return false;
      }

      return this.getMinutesSinceMidnight(slotDate) > nowMinutes;
    });
  }

  private getMinutesSinceMidnight(date: Date): number {
    return date.getHours() * 60 + date.getMinutes();
  }

  private formatDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  scrollDates(direction: 'prev' | 'next') {
    const currentIndex = this.dateScrollIndex;

    if (
      direction === 'next' &&
      currentIndex + this.visibleDatesCount < this.dates.length
    ) {
      this.dateScrollIndex = currentIndex + 1;
    } else if (direction === 'prev' && currentIndex > 0) {
      this.dateScrollIndex = currentIndex - 1;
    }

    this.updateVisibleDates();
  }

  private updateVisibleDates() {
    this.dates.forEach((date, index) => {
      date.isVisible =
        index >= this.dateScrollIndex &&
        index < this.dateScrollIndex + this.visibleDatesCount;
    });
  }

  onSlotSelect(slot: string) {
    this.selectedSlot = slot;
    this.notifyViewUpdate();
  }

  onTourFormCompleteChange(complete: boolean): void {
    this.surveyFormComplete = complete;
    this.notifyViewUpdate();
  }

  onTenantChange(tenantUuid: string): void {
    if (!tenantUuid || tenantUuid === this.selectedTenantUuid) {
      return;
    }

    this.selectedTenantUuid = tenantUuid;
    this.selectedSlot = null;
    this.availabilityLoadedStart = null;
    this.availabilityLoadedEnd = null;
    if (this.companyUuid) {
      void this.recaptchaFlow.prepareForTenant(this.companyUuid, tenantUuid);
    }
    this.loadAvailability();
  }

  get canSubmit(): boolean {
    return this.surveyFormComplete && !!this.selectedSlot;
  }

  formatTimeSlot(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  onRecaptchaV2Resolved(token: string): void {
    this.recaptchaFlow.onV2Resolved(token);
    this.notifyViewUpdate();
  }

  onRecaptchaV2Failed(): void {
    this.recaptchaFlow.onV2Failed();
    this.error.submission = 'Security verification failed. Please try again.';
    this.loading.submission = false;
    this.notifyViewUpdate();
  }

  async onSubmit() {
    const leadPayload = this.leadCapture()?.getLeadCaptureSubmissionPayload();
    if (!leadPayload || !this.selectedSlot || !this.selectedTenantUuid) {
      this.error.submission =
        'Please fill in all required fields and select a time slot';
      this.notifyViewUpdate();
      return;
    }

    this.loading.submission = true;
    this.error.submission = '';
    this.notifyViewUpdate();

    const submission: LeadCaptureWidgetSubmission = {
      ...leadPayload,
      widget_type: 'TourBooking',
      data: {
        ...leadPayload.data,
        tenant_uuid: leadPayload.data.tenant_uuid || this.selectedTenantUuid,
      },
      book_tour: {
        scheduled_datetime: this.selectedSlot,
        notes: String(leadPayload.data.note ?? ''),
      },
      trial_membership: '',
    };

    const tenantUuid = submission.data.tenant_uuid || this.selectedTenantUuid;
    await this.recaptchaFlow.beginSubmit(
      this.companyUuid ?? '',
      tenantUuid,
      (captcha) => {
        void this.executeTourBookingSubmit(submission, captcha);
      },
    );
    this.notifyViewUpdate();
  }

  private async executeTourBookingSubmit(
    submission: LeadCaptureWidgetSubmission,
    captcha?: WidgetRecaptchaSubmitPayload,
  ): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.tourBookingService.submitTourBooking(
          this.widgetId(),
          submission,
          captcha,
        ),
      );
      if (response?.id) {
        this.success = true;
        this.error = {};
        this.recaptchaFlow.resetChallenge();
        const tenantUuid = submission.data.tenant_uuid || this.selectedTenantUuid;
        if (this.companyUuid && tenantUuid) {
          void this.recaptchaFlow.prepareForTenant(this.companyUuid, tenantUuid);
        }
        this.resetForm();
      } else {
        throw new Error('No booking ID received from server');
      }
    } catch (error: unknown) {
      if (
        this.recaptchaFlow.handleRecaptchaError(error, (retryCaptcha) => {
          void this.executeTourBookingSubmit(submission, retryCaptcha);
        })
      ) {
        this.loading.submission = false;
        this.notifyViewUpdate();
        return;
      }

      this.error.submission = this.getSubmissionErrorMessage(error);
    } finally {
      this.loading.submission = false;
      this.notifyViewUpdate();
    }
  }

  private getSubmissionErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const fromApi = extractWidgetApiErrorMessage(error.error);
      if (fromApi) {
        return fromApi;
      }
      return error.message || 'Failed to book tour. Please try again.';
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Failed to book tour. Please try again.';
  }

  private resetForm() {
    this.selectedSlot = null;
    this.surveyFormComplete = false;
    this.selectedDateIndex = 0;
    this.leadCapture()?.resetSurvey();
    this.dateScrollIndex = 0;
    this.updateVisibleDates();
    this.availabilityLoadedStart = null;
    this.availabilityLoadedEnd = null;
    this.loadAvailability();
  }
}
